// ---------------------------------------------------------------------------
// SHOP PRICING — single source of truth for online card checkout.
//
// Only items listed here can be purchased with a card on the site. Everything
// else in the catalog (by-the-yard fabrics, multi-finish hardware, etc.) stays
// on the "request a quote" flow because pricing depends on options.
//
// Prices are in USD cents. Edit values here to change store pricing — then
// have the amounts updated in the payment provider to match.
//
// The keys are the catalog product ids, and they are also the payment
// provider price lookup keys.
// ---------------------------------------------------------------------------

export const SHOP_PRICES_CENTS: Record<string, number> = {
  // Cleaners & protectants
  "303-fabric-guard-non-pfas-32oz": 3295,
  "303-fabric-guard-non-pfas-1gal": 10995,
  "303-fabric-guard-non-pfas-16oz": 2195,
  "303-multi-surface-cleaner-1gal": 5995,
  "303-multi-surface-cleaner-16oz": 1295,
  "303-multi-surface-cleaner-32oz": 1795,
  "303-aerospace-protectant-2oz": 695,
  // Adhesives, tapes & sprays
  "xtreme-seal-pod9-black-100ft": 6995,
  "tear-mender-tm1-2oz": 995,
  "tear-mender-tg6h-6oz": 1695,
  "tear-mender-tg16-16oz": 2995,
  "tear-mender-tg640-5gal": 42995,
  "sprayway-silicone-sw945-11oz": 1295,
  // Thread (16-oz spools)
  "premobond-bpt-92-tex-90": 3495,
  "premobond-bpt-138-tex-135": 3795,
  "premobond-bpt-69-tex-70": 3295,
};

/** Flat rate shipping charged on orders under the free-shipping threshold. */
export const FLAT_SHIPPING_CENTS = 995;
/** Orders at or above this merchandise subtotal ship free. */
export const FREE_SHIPPING_THRESHOLD_CENTS = 7500;
/**
 * Display-only estimate. The exact rate for the buyer's Texas delivery address
 * is calculated by the payment provider at checkout (Texas local rates run
 * 6.25%–8.25%), so this value is never used to charge anyone.
 */
export const TAX_RATE = 0.0825;
/** Where local pickup orders are collected. */
export const PICKUP_LOCATION =
  "Texas Made Shade & Covers — 959 East Kingsbury Street, Seguin, TX 78155";
export const PICKUP_HOURS = "Mon–Fri 8:00am–4:30pm";

export const TAX_JURISDICTION = "Texas — exact rate calculated at checkout";


/**
 * Items we're out of. Add a product id here and it immediately stops being
 * purchasable online — the shop shows "Sold out" and the button becomes a
 * quote request instead. Remove the id when stock is back.
 */
export const SOLD_OUT_PRODUCT_IDS: string[] = [];

export function isSoldOut(productId: string): boolean {
  return SOLD_OUT_PRODUCT_IDS.includes(productId);
}

export function getPriceCents(productId: string): number | undefined {
  return SHOP_PRICES_CENTS[productId];
}

export function isPurchasable(productId: string): boolean {
  if (isSoldOut(productId)) return false;
  return typeof SHOP_PRICES_CENTS[productId] === "number";
}

export function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export type OrderTotals = {
  subtotalCents: number;
  shippingCents: number;
  /** Display-only estimate — the provider calculates the real amount at checkout. */
  estimatedTaxCents: number;
  /** Charged before tax (merchandise + shipping). Tax is added at checkout. */
  preTaxTotalCents: number;
  /** Display-only estimated grand total including the estimated tax. */
  totalCents: number;
  freeShipping: boolean;
  /** True when the customer picks the order up at our Seguin, TX shop. */
  pickup: boolean;
};

/**
 * Order math used by both the cart UI and the server. Merchandise + shipping is
 * what we charge; sales tax is calculated by the payment provider from the
 * buyer's Texas delivery address at checkout.
 */
export function calculateTotals(
  lines: { productId: string; quantity: number }[],
  options: { pickup?: boolean } = {},
): OrderTotals {
  const subtotalCents = lines.reduce((sum, line) => {
    const price = SHOP_PRICES_CENTS[line.productId] ?? 0;
    return sum + price * Math.max(1, Math.floor(line.quantity));
  }, 0);

  const pickup = options.pickup === true;
  const freeShipping = pickup || subtotalCents >= FREE_SHIPPING_THRESHOLD_CENTS;
  const shippingCents =
    pickup || subtotalCents === 0 || freeShipping ? 0 : FLAT_SHIPPING_CENTS;
  const preTaxTotalCents = subtotalCents + shippingCents;
  const estimatedTaxCents = Math.round(preTaxTotalCents * TAX_RATE);

  return {
    subtotalCents,
    shippingCents,
    estimatedTaxCents,
    preTaxTotalCents,
    totalCents: preTaxTotalCents + estimatedTaxCents,
    freeShipping,
    pickup,
  };
}

